import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { EmojiEvents, PublicOff, Warning } from "@mui/icons-material";

interface WorldCupModeConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isCurrentlyEnabled: boolean;
  loading: boolean;
}

const WorldCupModeConfirmDialog: React.FC<WorldCupModeConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  isCurrentlyEnabled,
  loading,
}) => {
  const willBeEnabled = !isCurrentlyEnabled;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          {willBeEnabled ? (
            <EmojiEvents sx={{ color: "#f5a623", fontSize: 28 }} />
          ) : (
            <PublicOff sx={{ color: "#9e9e9e", fontSize: 28 }} />
          )}
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {willBeEnabled
              ? "Enable Global World Cup Mode?"
              : "Enable Global Standard Mode?"}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box mb={2}>
          <Typography variant="body1" sx={{ fontWeight: "bold", mb: 1.5 }}>
            What this means:
          </Typography>
          <Box component="ul" sx={{ pl: 2, m: 0 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              {willBeEnabled
                ? "All active operators will immediately switch to World Cup Mode."
                : "All active operators will immediately switch to Standard Mode."}
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              {willBeEnabled
                ? "Users will see World Cup-themed visuals instead of standard visuals."
                : "Users will see Standard-themed visuals instead of World Cup visuals."}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            backgroundColor: willBeEnabled ? "#fff8e1" : "#f5f5f5",
            borderRadius: 2,
            border: willBeEnabled ? "1px solid #f5a623" : "1px solid #9e9e9e",
          }}
        >
          <Box display="flex" alignItems="flex-start" gap={1}>
            <Warning
              sx={{
                color: willBeEnabled ? "#f5a623" : "#9e9e9e",
                fontSize: 20,
                mt: 0.2,
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              WARNING: This change takes effect immediately for all active
              operators and their users.
            </Typography>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : willBeEnabled ? (
              <EmojiEvents />
            ) : (
              <PublicOff />
            )
          }
          sx={{
            backgroundColor: willBeEnabled ? "#f5a623" : "#9e9e9e",
            minWidth: 180,
            "&:hover": {
              backgroundColor: willBeEnabled ? "#e0941f" : "#757575",
            },
          }}
        >
          {loading
            ? "Processing..."
            : willBeEnabled
              ? "Enable World Cup Mode"
              : "Enable Standard Mode"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WorldCupModeConfirmDialog;
